import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useCurrentApplicationUuid } from "@lending/hooks/useCurrentApplicationUuid";
import { useFlowType } from "@lending/hooks/useFlowType";
import { getStepPath } from "@lending/navigation";
import { useStore } from "../../contexts/StoreContext";
import type { NavigationContext } from "@lending/navigation";
import type { ProductType } from "@lending/navigation/types";
import type { StateEnum } from "@lending/types";

interface ResolvedApplicationLayoutProps {
  fallbackPath: string;
  productType: ProductType;
  isLoading: boolean;
  isError: boolean;
  canonicalStepId?: string | null;
  applicationState?: StateEnum;
  navigationContext?: Partial<NavigationContext>;
  accountApplicationUuid?: string | null;
  shouldAlwaysEnforceRoute?: boolean;
}

function ResolvedApplicationLayout({
  fallbackPath,
  productType,
  isLoading,
  isError,
  canonicalStepId,
  applicationState,
  navigationContext,
  accountApplicationUuid,
  shouldAlwaysEnforceRoute = false,
}: ResolvedApplicationLayoutProps) {
  const location = useLocation();
  const flowType = useFlowType();
  const applicationUuid = useCurrentApplicationUuid();
  const { data: storeData, updateData, markInitialRouteResolved } = useStore();
  const hasWarmApplicationContext =
    Boolean(applicationUuid) && storeData.lendingApplicationID === applicationUuid;
  const hasResolvedInitialRoute = applicationUuid
    ? Boolean(storeData.resolvedInitialRouteByApplication?.[applicationUuid])
    : false;
  useEffect(() => {
    // Only hydrate the store with resolved values — writing mid-load would
    // mark the context warm while accountApplicationUuid is still unknown.
    if (!applicationUuid || isLoading || isError) {
      return;
    }

    updateData({
      lendingApplicationID: applicationUuid,
      applicationState,
      accountApplicationUuid: accountApplicationUuid ?? undefined,
      productType,
    });
  }, [
    applicationUuid,
    applicationState,
    accountApplicationUuid,
    isError,
    isLoading,
    productType,
    updateData,
  ]);

  useEffect(() => {
    if (!applicationUuid || isLoading || isError || hasResolvedInitialRoute) {
      return;
    }

    if (!canonicalStepId) {
      markInitialRouteResolved(applicationUuid);
      return;
    }

    const context = { ...navigationContext, applicationState, accountApplicationUuid };
    const canonicalPath = getStepPath(
      canonicalStepId,
      productType,
      flowType,
      context,
      applicationUuid
    );
    if (!canonicalPath || location.pathname === canonicalPath) {
      markInitialRouteResolved(applicationUuid);
    }
  }, [
    applicationUuid,
    canonicalStepId,
    flowType,
    hasResolvedInitialRoute,
    isError,
    applicationState,
    isLoading,
    location.pathname,
    markInitialRouteResolved,
    navigationContext,
    productType,
    accountApplicationUuid,
  ]);

  if (!applicationUuid) {
    return <Navigate to={`${fallbackPath}${location.search}`} replace />;
  }

  if (isError) {
    // TODO(portal-resume): differentiate auth failures (401/403) from other errors
    // and redirect to `/portal` for auth cases so a signed-out user landing on a
    // canonical application URL ends up back at the loan portal instead of the
    // generic fallback. This will need `useResolvedApplicationRoute` to expose
    // the error kind (e.g. `authError`) rather than a single `isError` boolean.
    return <Navigate to={`${fallbackPath}${location.search}`} replace />;
  }

  // Hold child routes until the store reflects this application. Rendering on
  // resolution alone lets children mount one commit before the store-hydration
  // effect runs, so they'd briefly see a missing (or stale) accountApplicationUuid.
  if (!hasWarmApplicationContext) {
    return null;
  }

  const shouldEnforceCanonicalRoute =
    shouldAlwaysEnforceRoute || !hasResolvedInitialRoute || canonicalStepId === "submitted";

  if (shouldEnforceCanonicalRoute && applicationUuid && canonicalStepId) {
    const context = { ...navigationContext, applicationState, accountApplicationUuid };
    const canonicalPath = getStepPath(
      canonicalStepId,
      productType,
      flowType,
      context,
      applicationUuid
    );

    if (canonicalPath && location.pathname !== canonicalPath) {
      return <Navigate to={`${canonicalPath}${location.search}`} replace />;
    }
  }

  return <Outlet />;
}

export { ResolvedApplicationLayout };
